import type { AxiosRequestConfig } from "axios";
import { http } from "./http";
import { refreshAccessToken } from "../api";
import type { User } from "../types/User";
import type {
  LoginResponse,
  RegisterFields,
  RegisterResponse,
  SignupPrecheck,
  ExchangeStripeReturnResponse,
  CompleteRegistrationOutcome,
  ResumeOnboardingResponse,
  ForgotPasswordResponse,
} from "../types/auth";

// The sign-in and sign-up doors. What the server hands back (a session
// token, or a Stripe link to open first) is routed by the page; the token
// itself goes to src/api.ts via setAccessToken.
export const authApi = {
  // ── session ──
  login: (form: { email: string; password: string }) => http.post<LoginResponse>("/auth/login", form),
  /** Shares any refresh already in flight — see refreshAccessToken in src/api.ts. */
  refreshToken: () => refreshAccessToken().then((accessToken) => ({ accessToken })),
  user: () => http.get<User>("/auth/user"),
  logout: () => http.post<unknown>("/auth/logout"),
  /** Warm the CSRF cookie before a state-changing call on a fresh page. */
  csrfToken: () => http.get<{ csrfToken: string }>("/csrf-token"),

  // ── Google / Apple ──
  /** Does this Google account already have a talent? (Also hands back the name + email for the sign-up form.) */
  googlePrecheck: (credential: string) => http.post<SignupPrecheck>("/auth/google/precheck", { credential }),
  applePrecheck: (idToken: string) => http.post<SignupPrecheck>("/auth/apple/precheck", { idToken }),
  googleLogin: (credential: string) => http.post<LoginResponse>("/auth/google/login", { credential }),
  appleLogin: (idToken: string) => http.post<LoginResponse>("/auth/apple/login", { idToken }),
  googleRegister: (body: RegisterFields & { credential: string }) => http.post<RegisterResponse>("/auth/google/register", body),
  appleRegister: (body: RegisterFields & { idToken: string }) => http.post<RegisterResponse>("/auth/apple/register", body),

  // ── password reset ──
  forgotPassword: (email: string, client: "app" | "web") => http.post<ForgotPasswordResponse>("/auth/forgot-password", { email, client }),
  resetPasswordInfo: (token: string) => http.get<{ email?: string }>(`/auth/reset-password/${token}`),
  resetPassword: (token: string, password: string) => http.post<unknown>(`/auth/reset-password/${token}`, { password }),

  // ── early-access code (sign-up) ──

  // ── the Stripe round trip ──
  exchangeStripeReturn: (token: string) => http.post<ExchangeStripeReturnResponse>("/auth/exchange-stripe-return", { token }),
  completeRegistration: (query: string, body: Record<string, unknown>) =>
    http.post<CompleteRegistrationOutcome>(`/auth/complete-registration?${query}`, body),
  resumeOnboarding: (body: Record<string, unknown>) => http.post<ResumeOnboardingResponse>("/auth/resume-onboarding", body),

  // ── the desktop companion hand-off ──
  desktopHandoff: (accessToken: string) =>
    http.post<{ code?: string }>("/auth/desktop/handoff", {}, { headers: { Authorization: `Bearer ${accessToken}` } } as AxiosRequestConfig),
  desktopExchange: (code: string) => http.post<LoginResponse>("/auth/desktop/exchange", { code }),
};
